import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Tracking } from '@panamax/app-state';
import { ExceptionResolution, Order, OrderItem } from '@usf/ngrx-order';
import { Product } from 'src/app/shared/models/product.model';
import { QuantityType } from 'src/app/shared/models/quantity-type';
import {
  AnalyticsProduct,
  AnalyticsSubstituteProduct,
} from 'src/app/shared/models/tracking/analytics/analytics-product';
import { SharedTrackingService } from 'src/app/shared/services/analytics/shared-tracking.service';
import { ExceptionType } from '../../constants/order-exception-type.enum';
import { SubOutAnalyticsProduct } from '../models/sub-out-analytics-product';
import { SubstitutionAnalyticsProduct } from '../models/substitution-analytics-product';
import { ORDER_EXCEPTIONS_ANALYTICS_ACTIONS } from './order-exceptions-analytics.actions';
import { OrderExceptionsAnalyticsMessages } from './order-exceptions-analytics.constants';
import {
  defaultTransformerFunc,
  submitOrder,
} from './order-exceptions-analytics.transformers';
import { OrderExceptionViewModel } from '../../models/order-exception-view.model';
import { OrderAnalyticsService } from 'src/app/order/tracking/analytics/order-analytics.service';
@Injectable({
  providedIn: 'root',
})
export class OrderExceptionsAnalyticsService {
  constructor(private store: Store) {}

  /**
   *
   * Create analytics event for submitting an order with exceptions.
   * @param order Client Domain Order
   * @param orderExceptions Client Domain Map of Order Exceptions
   * @param productMap All products in the view model
   * @param divisionNumber For the logged in customer
   * There are really 3 types of product models being returned
   * Not subbed:  All products not Subbed or SearchSubbed
   * Subbed: Will have a resolutionOrderItem.substituteProduct from SubOrReplaceItems
   * SearchSubbed: also a resolutionOrderItem.substituteProduct but not from SubOrReplaceItems
   */
  submitOrder(
    order: Order,
    orderExceptions: Map<ExceptionType, ExceptionResolution[]>,
    productMap: Map<number, Product>,
    divisionNumber: number,
  ): void {
    // All Product Numbers on the order
    const productNumbers = order.orderItems.map(oi => oi.productNumber);

    // Get resolutions for all types of substitution resolutions
    const oos = orderExceptions?.get(ExceptionType.OUT_OF_STOCK);
    const unavailable = orderExceptions?.get(ExceptionType.PRODUCT_UNAVAILABLE);
    const restricted = orderExceptions?.get(ExceptionType.RESTRICTED);
    let subResolutions: ExceptionResolution[] = [];
    if (oos) {
      subResolutions = subResolutions.concat(oos);
    }
    if (unavailable) {
      subResolutions = subResolutions.concat(unavailable);
    }
    if (restricted) {
      subResolutions = subResolutions.concat(restricted);
    }
    subResolutions = subResolutions?.filter(
      resolution => !!resolution.resolutionOrderItem?.substituteProduct,
    );

    // Original Product Numbers with resolutionOrderItem.substituteProduct
    const productNumbersWithSubs = subResolutions?.map(
      resolution =>
        resolution.resolutionOrderItem.originalProduct.productNumber,
    );
    // Product numbers without substitutions
    const productNumbersWithoutSubs = productNumbers.filter(
      p => !productNumbersWithSubs?.includes(p),
    );
    // Order Items for products without subs
    const orderItemsWithOutSubs = order.orderItems.filter(oi =>
      productNumbersWithoutSubs.includes(oi.productNumber),
    );

    // Product tracking data for nonSubstitutedProducts
    let analyticsProducts = this.nonSubstitutedProducts(
      orderItemsWithOutSubs,
      productMap,
      divisionNumber,
    );
    // Get analytic products for substituted in from search from sub option
    const subbedInProducts = this.subInProducts(
      productMap,
      subResolutions,
      divisionNumber,
    );
    // Get analytic products for substituted out
    const subbedOutProducts = this.subOutProducts(
      productMap,
      subResolutions,
      divisionNumber,
    );
    if (subbedInProducts) {
      analyticsProducts = analyticsProducts.concat(subbedInProducts);
    }
    if (subbedOutProducts) {
      analyticsProducts = analyticsProducts.concat(subbedOutProducts);
    }

    const tracking: Tracking = {
      analytics: {
        data: {
          event: OrderExceptionsAnalyticsMessages.submitEvent,
          capability: OrderExceptionsAnalyticsMessages.capability,
          openMethod: OrderExceptionsAnalyticsMessages.openMethod,
          orderId: OrderAnalyticsService.getAnalyticsOrderId(
            order?.orderHeader,
          ),
          products: analyticsProducts,
        },
        transformFunc: submitOrder,
      },
    };
    this.store.dispatch(
      ORDER_EXCEPTIONS_ANALYTICS_ACTIONS.submitOrder({ tracking }),
    );
  }

  /**
   *
   * @param orderItems Array of Client Domain OrderItems
   * @param productMap Product Map of product number to summary/inventory/contract
   * @param divisionNumber For prepending to product number
   * @returns Array of analytics products that dont have exception resolutions
   */
  private nonSubstitutedProducts(
    orderItems: OrderItem[],
    productMap: Map<number, Product>,
    divisionNumber: number,
  ): AnalyticsProduct[] {
    const products = SharedTrackingService.analyticsProductData(
      orderItems,
      productMap,
      divisionNumber,
    ) as AnalyticsProduct[];
    products.forEach(product => (product.orderedOriginal = '1'));
    return products;
  }

  /**
   *
   * @param orderExceptions Client domain map of exception types to resolutions
   * @param productNumbersWithSubs Product number to create analytics for
   * @returns Analytics Products arrary for products subbed in from initial product list
   */
  private subInProducts(
    productMap: Map<number, Product>,
    substituteResolutions: ExceptionResolution[],
    divisionNumber: number,
  ): SubstitutionAnalyticsProduct[] {
    let products: SubstitutionAnalyticsProduct[];
    if (substituteResolutions) {
      products = substituteResolutions.map(res => {
        let subAnalyticsProduct: SubstitutionAnalyticsProduct;
        const originalProduct = productMap.get(
          res.resolutionOrderItem.originalProduct.productNumber,
        );
        const substituteProduct = productMap.get(
          res.resolutionOrderItem.substituteProduct.productNumber,
        );

        if (substituteProduct) {
          subAnalyticsProduct = {
            divisionApn: `${divisionNumber}-${substituteProduct.productNumber}`,
            quantity:
              res.resolutionOrderItem.substituteProduct.unitsOrdered
                .currentValue,
            quantityUnits: QuantityType.Cases,
            attributes: substituteProduct?.trackingAttributes,
            addMethod: 'exceptions',
            subIn: '1',
            substitutionParentApn: `${divisionNumber}-${originalProduct.productNumber}`,
          };
          if (!this.subNumberInSubOrReplaceItems(substituteProduct, res)) {
            subAnalyticsProduct.searchForSubstitution = '1';
          }
        }
        return subAnalyticsProduct;
      });
    }

    return products;
  }

  /**
   *
   * @param orderExceptions Client domain map of exception types to resolutions
   * @param productNumbersWithSubs Product number to create analytics for
   * @returns Analytics Products arrary for the original products subbed out
   */
  private subOutProducts(
    productMap: Map<number, Product>,
    substituteResolutions: ExceptionResolution[],
    divisionNumber,
  ): SubOutAnalyticsProduct[] {
    let products: SubOutAnalyticsProduct[];

    if (substituteResolutions) {
      products = substituteResolutions.map(res => {
        const originalProduct = productMap.get(
          res.resolutionOrderItem.originalProduct.productNumber,
        );
        if (originalProduct) {
          const subOutAnalyticsProduct: SubOutAnalyticsProduct = {
            divisionApn: `${divisionNumber}-${originalProduct.productNumber}`,
            attributes: originalProduct?.trackingAttributes,
            subOut: '1',
          };

          return subOutAnalyticsProduct;
        }
      });
    }

    return products;
  }
  /**
   *
   * @param substituteProduct Client Domain Product
   * @param resolution Client Domain ExceptionResolution
   * @returns true if substitute was in the SubOrReplaceItems
   */
  private subNumberInSubOrReplaceItems(
    substituteProduct: Product,
    resolution: ExceptionResolution,
  ): boolean {
    return !!resolution.orderException.subOrReplaceItems?.find(
      sri => sri.productNumber === substituteProduct.productNumber,
    );
  }

  trackPageLoad(
    order: Order,
    productTracking: AnalyticsProduct[] | AnalyticsSubstituteProduct[],
  ) {
    const tracking: Tracking = {
      analytics: {
        data: {
          event: OrderExceptionsAnalyticsMessages.pageLoadedEvent,
          capability: OrderExceptionsAnalyticsMessages.capability,
          page: {
            pageName: OrderExceptionsAnalyticsMessages.pageName,
          },
          order: {
            id: OrderAnalyticsService.getAnalyticsOrderId(order?.orderHeader),
            openMethod: OrderExceptionsAnalyticsMessages.openMethod,
          },
          products: productTracking,
        },
        transformFunc: defaultTransformerFunc,
      },
    };
    this.store.dispatch(
      ORDER_EXCEPTIONS_ANALYTICS_ACTIONS.trackPageLoad({ tracking }),
    );
  }

  trackRemoveOOSProduct(
    productMap: Map<number, Product>,
    vm: OrderExceptionViewModel,
  ) {
    const productNumber = productMap.entries().next().value[0];
    const orderItem = vm?.order?.orderItems.find(
      oi => oi.productNumber === productNumber,
    );
    const analyticsProduct = SharedTrackingService.analyticsProductData(
      [orderItem],
      productMap,
      vm?.order?.orderHeader?.divisionNumber,
    ) as AnalyticsProduct[];
    const tracking: Tracking = {
      analytics: {
        data: {
          event: OrderExceptionsAnalyticsMessages.removeFromOrder,
          capability: OrderExceptionsAnalyticsMessages.capability,
          order: {
            id: OrderAnalyticsService.getAnalyticsOrderId(
              vm?.order?.orderHeader,
            ),
          },
          products: analyticsProduct,
        },
        transformFunc: defaultTransformerFunc,
      },
    };
    this.store.dispatch(
      ORDER_EXCEPTIONS_ANALYTICS_ACTIONS.trackRemoveOOSProduct({ tracking }),
    );
  }
}
